// Where this account is signed in, and closing the places that are not here.
//
// ================================ WHY NOTHING IS SHOWN UNTIL SOMEBODY ASKS
//
// Every other panel on the account screen draws itself. This one is blank until
// a person types six digits, and that is the feature rather than a loading
// state. The list is the one thing on this console a thief would want: it says
// how many ways into this account are open and when each was last used. The
// service will not answer it for a session token alone — see api/signIns.ts —
// so there is nothing to draw on arrival and asking early would only spend
// somebody's code for a screen they were walking past.
//
// ============================================ WHY IT ASKS TWICE, WHICH IS NOT A BUG
//
// Reading the list costs a code. Ending the other sessions costs another one,
// and they cannot be the same six digits: `verifyAndClaim` spends a code against
// `second_factor.last_time_step`, so the second request inside one thirty-second
// window is refused as a replay. That is the same property that stops somebody
// who watched you type from reusing what they saw.
//
// It could have been avoided by having the read hand back a short-lived grant
// for the write. That is the inherited proof this whole design refuses, one step
// smaller: a grant sitting in a browser is a thing that can be taken, and the
// point of the write is to evict somebody who has taken something. So the second
// code is asked for, the wait is explained where somebody meets it rather than
// in a help page, and the cost is thirty seconds in the rare event that a person
// is evicting an intruder.

import { useState, type ReactNode } from "react";
import { NoticeBox } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { endOtherSignIns, listSignIns, type SignIn, type SignInRefusal } from "../api/signIns.js";
import type { ApiOptions } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { FACTOR_FIELD } from "./Authorise.js";

export type SignInsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  /** Called when the service says this session is over, exactly as a report 401 does. */
  readonly onSessionEnded: (detail: string) => void;
};

type Phase =
  | { readonly kind: "closed" }
  | { readonly kind: "open"; readonly sessions: readonly SignIn[]; readonly detail: string };

export function SignIns({ api, token, onSessionEnded }: SignInsProps): ReactNode {
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<SignInRefusal | null>(null);
  const [ended, setEnded] = useState<string | null>(null);

  // One handler for both requests, because they fail in exactly the same ways
  // and a session that ended during either is the same event. What differs is
  // only what a success means, which is the caller's business.
  const run = async <T,>(work: () => Promise<Awaited<ReturnType<typeof listSignIns>> | T>) => {
    setBusy(true);
    setRefusal(null);
    try {
      return await work();
    } finally {
      setBusy(false);
      // The code is cleared whatever happened, including on success. It has been
      // spent either way, and leaving six digits sitting in a field invites
      // somebody to press the next button with them and be told they are wrong.
      setCode("");
    }
  };

  const reveal = async () => {
    const result = await run(() => listSignIns(api, token, code));
    if (result.ok) {
      setPhase({ kind: "open", sessions: result.value.sessions, detail: result.value.detail });
      setEnded(null);
      return;
    }
    if (result.kind === "session_ended") return onSessionEnded(result.detail);
    setRefusal(result);
  };

  const endOthers = async () => {
    const result = await run(() => endOtherSignIns(api, token, code));
    if (result.ok) {
      setEnded(result.value.detail);
      // The list on screen is now wrong — it still shows the sessions that were
      // just closed. Emptied rather than left standing, because a stale list
      // here reads as "that did not work". The person can read it again.
      setPhase({ kind: "closed" });
      return;
    }
    if (result.kind === "session_ended") return onSessionEnded(result.detail);
    setRefusal(result);
  };

  return (
    <section>
      <SecLabel>Active sign-ins</SecLabel>
      <div className="panel panel-stack">
        <p className="help help-tight">
          View every active browser session. A current authenticator code is required.
        </p>

        {ended === null ? null : (
          <NoticeBox kind="good" title="Done">
            {ended}
          </NoticeBox>
        )}

        {refusal === null ? null : (
          <NoticeBox
            // Amber for the two a person fixes by typing again, red for the
            // three they cannot fix from this screen at all.
            kind={refusal.kind === "code_refused" || refusal.kind === "code_required" ? "amber" : "bad"}
            title={
              refusal.kind === "code_refused"
                ? "Code not accepted"
                : refusal.kind === "no_authenticator"
                  ? "No authenticator is set up"
                  : refusal.kind === "not_offered"
                    ? "This feature is not available"
                    : "Could not load sign-ins"
            }
          >
            {refusal.detail}
          </NoticeBox>
        )}

        {phase.kind === "open" ? (
          <>
            <SignInTable sessions={phase.sessions} />
            {phase.detail === "" ? null : <p className="help">{phase.detail}</p>}

            {phase.sessions.filter((s) => !s.current && s.revokedAt === null).length === 0 ? (
              <p className="help">This is the only active sign-in.</p>
            ) : (
              <form
                className="panel-stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  void endOthers();
                }}
              >
                <p className="help">
                  This keeps your current sign-in open. Wait for a new authenticator code before
                  continuing.
                </p>
                <CodeField
                  id="console-signins-end-code"
                  label="Authenticator code"
                  value={code}
                  onChange={setCode}
                  busy={busy}
                  onEnter={() => void endOthers()}
                />
                <div className="acts acts-flush">
                  <button type="submit" className="btn btn-quiet" disabled={busy || code.trim() === ""}>
                    {busy ? "Ending…" : "Sign out every other session"}
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          <form
            className="panel-stack"
            onSubmit={(event) => {
              event.preventDefault();
              void reveal();
            }}
          >
            <CodeField
              id="console-signins-read-code"
              label="Authenticator code"
              value={code}
              onChange={setCode}
              busy={busy}
              onEnter={() => void reveal()}
            />
            <div className="acts acts-flush">
              <button type="submit" className="btn" disabled={busy || code.trim() === ""}>
                {busy ? "Checking…" : "View sign-ins"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

/**
 * The list itself.
 *
 * A TABLE WITH NO ADDRESS COLUMN AND NO DEVICE COLUMN, which is the first thing
 * anybody will look for. The service records neither: 006 keeps `requested_ip`
 * on `login_code` alone, deliberately and briefly, and nothing has ever recorded
 * a user agent. The sentence under the table says so, because a reader who is
 * not told will assume the columns were withheld for the row they are worried
 * about.
 *
 * What is left still answers the question people come here with. Two live
 * sign-ins when you only have one browser open is the signal, and it does not
 * need a city name to be alarming.
 */
function SignInTable({ sessions }: { sessions: readonly SignIn[] }): ReactNode {
  if (sessions.length === 0) {
    return <p className="help">No sign-ins were found.</p>;
  }

  return (
    // Wrapped, as every other table on this console is bar one. Three columns
    // of locale timestamps is wider than it looks — "07/09/2026, 18:41:23" twice
    // over plus a state sentence — and the account screen is as likely to be
    // open in a half-width window beside something else as it is full screen.
    // Without this the row does not wrap, it clips.
    <div className="tablewrap">
      <div className="tablescroll">
      <table>
        <thead>
          <tr>
            <th scope="col">Signed in</th>
            {/* NOT "Last used", WHICH THIS COLUMN CANNOT SUPPORT. Only a report
                read stamps `auth_session.last_seen_at` — every other path
                declines on purpose, so that a session cannot be kept alive by
                its own housekeeping — so a session that signed in and managed
                settings all afternoon still shows nothing here. Headed "Last
                report" the empty cells are a fact; headed "Last used" they are a
                lie about the row somebody is trying to judge. */}
            <th scope="col">Last report</th>
            <th scope="col">State</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.sessionId}>
              <td data-label="Signed in">
                {when(s.issuedAt)}
                {s.current ? <span className="quiet"> · this one</span> : null}
              </td>
              {/* Said in words rather than left as a dash, which reads as
                  missing data — and said as "no report" rather than "not used",
                  because that is the only claim the column can carry. */}
              <td data-label="Last report">{s.lastSeenAt === null ? "No report opened" : when(s.lastSeenAt)}</td>
              <td data-label="State">{state(s)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

/**
 * What a row's state is, in the reader's terms rather than the column's.
 *
 * `revoked_by` is a category and not a person — 015 is explicit that no path
 * writes an actor there — so "ended" is as far as this can honestly go, except
 * for `token_reused`, which is the service revoking a session about itself and
 * is the single most important row this table can contain: it means a token for
 * this account was presented twice after being rotated away, which is what a
 * copied session looks like.
 */
function state(s: SignIn): string {
  if (s.revokedBy === "token_reused") return "Ended by the service: a token was used twice";
  if (s.revokedAt !== null) return "Ended";
  if (new Date(s.expiresAt).getTime() <= Date.now()) return "Expired";
  return "Live";
}

function when(iso: string): string {
  const at = new Date(iso);
  return isNaN(at.getTime()) ? iso : at.toLocaleString();
}

function CodeField({
  id,
  label,
  value,
  onChange,
  busy,
  onEnter,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  busy: boolean;
  onEnter: () => void;
}): ReactNode {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input mono"
        // `text` rather than `number`, on SignIn.tsx's reasoning: a number input
        // strips the leading zero a six-digit code can start with.
        type="text"
        inputMode="numeric"
        {...FACTOR_FIELD}
        name="xp-factor"
        maxLength={8}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || busy || value.trim() === "") return;
          event.preventDefault();
          onEnter();
        }}
        disabled={busy}
        required
      />
    </div>
  );
}
